import { Component, ViewChild } from '@angular/core';
import { Workflow } from '../Model/Dto/Workflow/Workflow';
import { WorkflowService } from '../Services/Workflow/workflow.service';
import { LoginService } from '../Services/login.service';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkflowItem } from '../Model/Dto/Workflow/WorkflowItem';
import { SystemService } from '../Services/system.service';
import { DxAccordionComponent } from 'devextreme-angular';
import { custom } from 'devextreme/ui/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Message } from '../Model/System/Message';
import { CatalogService } from '../Services/CatalogManagement/catalog.service';

@Component({
  selector: 'workflows',
  templateUrl: './workflows.component.html',
  styleUrl: './workflows.component.css'
})
export class WorkflowsComponent {
  @ViewChild(DxAccordionComponent, { static: true }) accordion: DxAccordionComponent;

  workflows: Workflow[] = [];
  popupExistingWorkflowplansVisible: boolean = false;
  linkList = [];

  private allWorkflows: Workflow[] = [];
  globalSearchTerm: string = '';

  constructor(
    public workflowService: WorkflowService,
    public loginService: LoginService,
    public systemService: SystemService,
    public translate: TranslateService,
    public catalogService: CatalogService
  ) {

    this.createNewWorkflow = this.createNewWorkflow.bind(this);

    this.reloadWorkflows();
  }

  private normalize(value: unknown): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private workflowNameMatches(workflow: Workflow, normalizedTerm: string): boolean {
    if (!normalizedTerm) return true;
    return this.normalize(workflow?.name).includes(normalizedTerm);
  }

  private workflowItemMatches(item: WorkflowItem, normalizedTerm: string): boolean {
    if (!normalizedTerm) return true;
    const type = this.normalize((item)?.workflowItemType);
    const macroName = this.normalize((item)?.macroName);

    return (
      type.includes(normalizedTerm) ||
      macroName.includes(normalizedTerm)
    );
  }

    private applyGlobalFilter() {
    const term = this.normalize(this.globalSearchTerm);

    const acc = this.accordion?.instance as any;
    const prevSelectedIndex: number =
      typeof acc?.option === 'function' ? acc.option('selectedIndex') : -1;
    const prevSelectedId = prevSelectedIndex > -1 ? this.workflows[prevSelectedIndex]?.id : null;

    if (!term) {
      this.workflows = this.allWorkflows.map(w => ({
        ...w,
        items: Array.isArray(w.items) ? [...w.items] : []
      }));
      // Expand-Zustand nach Datenbindung wiederherstellen
      setTimeout(() => {
        if (!acc) return;
        let idx = -1;
        if (prevSelectedId) {
          idx = this.workflows.findIndex(w => w?.id && w.id === prevSelectedId);
        } else if (prevSelectedIndex > -1 && this.workflows[prevSelectedIndex]) {
          idx = prevSelectedIndex;
        }
        acc.option?.('selectedIndex', idx);
        acc.updateDimensions?.();
      });
      return;
    }

    const filteredWorkflows: Workflow[] = [];

    for (const workflow of this.allWorkflows) {
      const nameMatches = this.workflowNameMatches(workflow, term);

      if (nameMatches) {
        filteredWorkflows.push({
          ...workflow,
          items: Array.isArray(workflow.items) ? [...workflow.items] : []
        });
        continue;
      }

      const items = (workflow.items ?? []).filter((item: WorkflowItem) =>
        this.workflowItemMatches(item, term)
      );

      if (items.length > 0) {
        filteredWorkflows.push({
          ...workflow,
          items
        });
      }
    }

    this.workflows = filteredWorkflows;

    // Expand-Zustand nach Filterung bestmöglich erhalten
    setTimeout(() => {
      if (!acc) return;
      let idx = -1;
      if (prevSelectedId) {
        idx = this.workflows.findIndex(w => w?.id && w.id === prevSelectedId);
      } else if (prevSelectedIndex > -1 && this.workflows[prevSelectedIndex]) {
        idx = prevSelectedIndex;
      }
      acc.option?.('selectedIndex', idx);
      acc.updateDimensions?.();
    });
  }

  private isWorkflowExpanded(workflow: Workflow): boolean {
    const acc = this.accordion?.instance as any;
    if (!acc) return false;
    const index = this.workflows.findIndex(x =>
      x === workflow || (!!x?.id && !!workflow?.id && x.id === workflow.id)
    );
    const selectedIndex: number =
      typeof acc.option === 'function' ? acc.option('selectedIndex') : -1;
    return index > -1 && index === selectedIndex;
  }

  private expandAccordionForWorkflow(workflow: Workflow): void {
    const findIndex = () =>
      this.workflows.findIndex(x =>
        x === workflow ||
        (!!x?.id && !!workflow?.id && x.id === workflow.id) ||
        (!workflow?.id && !!x?.name && x.name === workflow?.name)
      );

    // Wenn bereits offen, nichts tun -> vermeidet Close/Open-Flicker
    if (this.isWorkflowExpanded(workflow)) {
      (this.accordion?.instance as any)?.updateDimensions?.();
      return;
    }

    // Ansonsten sanft expandieren (nach Render-Zyklus)
    setTimeout(() => {
      const index = findIndex();
      if (index < 0) return;
      try {
        (this.accordion?.instance as any)?.option?.('selectedIndex', index);
        (this.accordion?.instance as any)?.updateDimensions?.();
      } catch {
        setTimeout(() => {
          const retryIndex = findIndex();
          if (retryIndex >= 0) {
            (this.accordion?.instance as any)?.option?.('selectedIndex', retryIndex);
            (this.accordion?.instance as any)?.updateDimensions?.();
          }
        });
      }
    });
  }

  onGlobalFilterChange(event: { value?: unknown }){
    this.globalSearchTerm = (event?.value ?? '').toString();
    this.applyGlobalFilter();
  }

  createNewWorkflow(){
    const newWorkflow: Workflow = new Workflow(
      null,
      this.translate.instant('Workflownamen vergeben')
    );
    newWorkflow.editMode = true;

    const initializedNewWorkflow: Workflow = {
      ...newWorkflow,
      items: Array.isArray(newWorkflow.items) ? newWorkflow.items : []
    } as Workflow;

    this.allWorkflows = [...this.allWorkflows, initializedNewWorkflow];

    this.applyGlobalFilter();

    this.workflowService
      .updateOrCreateWorkflow(newWorkflow, this.loginService.currentCustomer.id)
      .then((_result: Workflow) => this.reloadWorkflows())
      .catch(error => {
        const _response = new HttpErrorResponse(error);
        this.systemService.notifyInfo(this.translate.instant('Fehler beim Anlegen des Workflows'));
        this.reloadWorkflows();
      });
  }

  toggleWorkflowEditMode(event, workflow: Workflow){
    event.event.stopPropagation();
    workflow.editMode = !workflow.editMode;
    this.accordion.instance.repaint();
  }

  async updateWorkflow(event: any, workflow: Workflow): Promise<void> {
    event.event.stopPropagation();
    this.toggleWorkflowEditMode(event, workflow);
    await this.workflowService.updateOrCreateWorkflow(workflow, this.loginService.currentCustomer.id);
  }

  async copyWorkflow(event, workflow: Workflow): Promise<void> {
    const workflowDuplicate: Workflow = new Workflow(
      undefined,
      workflow.name + this.translate.instant('Duplikat'),
      workflow.items,
      false
    );
    await this.workflowService.updateOrCreateWorkflow(workflowDuplicate, this.loginService.currentCustomer.id);
    this.reloadWorkflows();
  }

  private resetFilter(): void {
    if (this.globalSearchTerm) {
      this.globalSearchTerm = '';
      this.applyGlobalFilter();
    }
  }

  addWorkflowItem(event, workflow: Workflow){
    event.event.stopPropagation();

    const wasExpanded = this.isWorkflowExpanded(workflow);

    this.resetFilter();

    const sourceIndex = this.allWorkflows.findIndex(w => w.id === workflow.id);
    const sourceWorkflow = sourceIndex >= 0 ? this.allWorkflows[sourceIndex] : null;
    const nextOrder = sourceWorkflow && Array.isArray(sourceWorkflow.items) && sourceWorkflow.items.length > 0
      ? Math.max(...sourceWorkflow.items.map(i => (typeof (i as any).order === 'number' ? (i as any).order : 0))) + 1
      : 1;

    const newItem: WorkflowItem = new WorkflowItem(nextOrder);

    const updatedVisibleItems = [...(workflow.items ?? []), newItem];
    workflow.items = updatedVisibleItems;

    if (sourceWorkflow) {
      const updatedSourceItems = [...(sourceWorkflow.items ?? []), newItem];
      this.allWorkflows[sourceIndex] = { ...sourceWorkflow, items: updatedSourceItems };
    }

    if (this.globalSearchTerm) {
      this.applyGlobalFilter();
      this.expandAccordionForWorkflow(workflow);
    } else {
      (this.accordion?.instance as any)?.updateDimensions?.();
      if (!wasExpanded) {
        this.expandAccordionForWorkflow(workflow);
      }
    }

    this.workflowService.updateOrCreateWorkflow(workflow, this.loginService.currentCustomer.id);
  }

  deleteWorkflow = async (event, workflow: Workflow): Promise<void> => {
    let myDialog: any;
    let txtYes = '';
    let txtNo = '';
    let txtMsg = '';
    let txtTitle = '';
    const rootUrl = window.location.origin;
    const customerId = this.loginService.currentCustomer.id;

    const existingPlans = await this.workflowService.getAllWorkflowplansByWorkflowId(workflow.id, customerId);

    if (existingPlans.length > 0) {
      for (const plan of existingPlans) {
        const catalogName = await this.catalogService.getCatalogName(plan.catalogId, customerId);
        this.linkList.push({
          text: catalogName,
          url: `${rootUrl}/catalog/${customerId}/${plan.catalogId}/workflowplans`
        });
      }
      this.popupExistingWorkflowplansVisible = true;
    } else {
      this.translate.get('Ja').subscribe((text: string) => (txtYes = text));
      this.translate.get('Nein').subscribe((text: string) => (txtNo = text));
      this.translate.get('Wollen Sie den Workflow wirklich löschen').subscribe((text: string) => {
        txtMsg = text;
        if (!txtMsg.endsWith('?')) txtMsg = `${txtMsg}?`;
      });
      this.translate.get('Wirklich löschen?').subscribe((text: string) => (txtTitle = text));

      myDialog = custom({
        title: txtTitle,
        messageHtml: txtMsg,
        buttons: [
          {
            text: txtYes,
            onClick: async () => {
              await this.workflowService.deleteWorkflow(workflow, this.loginService.currentCustomer.id);
              this.reloadWorkflows();
            }
          },
          {
            text: txtNo,
            onClick: () => true
          }
        ]
      });
      myDialog.show();
    }
  };

  reloadWorkflows(){
    this.workflowService.getWorkflows(this.loginService.currentCustomer.id).then((result: Workflow[]) => {
      this.allWorkflows = Array.isArray(result) ? result : [];
      this.allWorkflows = this.allWorkflows.map(w => ({
        ...w,
        items: Array.isArray(w.items) ? w.items : []
      }));
      this.applyGlobalFilter();
    });
  }
}
